import { useState, useEffect } from "react";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Alert, AlertDescription } from "../components/ui/alert";
import { Badge } from "../components/ui/badge";
import { Leaf, Mail, User, Loader2, ArrowRight, Building2, UserPlus, AlertCircle } from "lucide-react";
import { motion } from "framer-motion";
import { signUpWithEmail } from "../lib/api";
import { toast } from "sonner";
import { projectId, publicAnonKey } from "../lib/config";

interface SignUpProps {
  onNavigate?: (page: string) => void;
}

interface Invitation {
  id: string;
  email: string;
  role: string;
  clinic_name: string;
  invited_by_name: string;
  created_at: string;
  expires_at: string;
}

export function SignUp({ onNavigate }: SignUpProps) {
  const [email, setEmail] = useState("patient@cannabias.co.uk");
  const [displayName, setDisplayName] = useState("Test Patient");
  const [loading, setLoading] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [invitation, setInvitation] = useState<Invitation | null>(null);
  const [invitationLoading, setInvitationLoading] = useState(false);
  const [invitationError, setInvitationError] = useState<string | null>(null);

  // Check URL for invitation parameter and dev mode state
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const state = urlParams.get('state');
    const invitationId = urlParams.get('invitation');
    
    if (state === 'emailsent') {
      setEmailSent(true);
      console.log('🔧 DEV MODE: Showing email sent screen');
    }

    if (invitationId) {
      loadInvitation(invitationId);
    }
  }, []);

  const loadInvitation = async (invitationId: string) => {
    try {
      setInvitationLoading(true);
      setInvitationError(null);

      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-cc125661/org/invitation/${invitationId}`,
        {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${publicAnonKey}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Invalid invitation');
      }

      const data = await response.json();
      setInvitation(data);
      setEmail(data.email); // Pre-fill email
      console.log('📧 Invitation loaded:', data);
    } catch (err: any) {
      console.error('Error loading invitation:', err);
      setInvitationError(err.message || 'Failed to load invitation');
      toast.error(err.message || 'Invalid or expired invitation');
    } finally {
      setInvitationLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email || !displayName) {
      toast.error("Please fill in all fields");
      return;
    }

    // If invitation exists, verify email matches
    if (invitation && email.toLowerCase() !== invitation.email.toLowerCase()) {
      toast.error("Email must match the invitation email address");
      return;
    }

    setLoading(true);

    try {
      // Store invitation ID in localStorage so we can use it after email verification
      if (invitation) {
        localStorage.setItem('pending_invitation', invitation.id);
      }

      await signUpWithEmail(email, displayName);
      setEmailSent(true);
      toast.success("Check your email for the magic link!");
    } catch (error: any) {
      console.error("Sign up error:", error);
      toast.error(error.message || "Failed to sign up. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (emailSent) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4 sm:px-6 lg:px-8 gradient-mesh">
        <div className="w-full max-w-md">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.4 }}
          >
            <Card className="p-10 glass-card text-center">
              <div className="w-16 h-16 rounded-full gradient-green flex items-center justify-center mx-auto mb-6 shadow-xl">
                <Mail className="w-8 h-8 text-white" />
              </div>

              <h2 className="text-3xl mb-3">Check your email</h2>

              <p className="text-muted-foreground mb-6">
                We've sent a magic link to <strong>{email}</strong>
              </p>

              <div className="p-4 rounded-xl bg-accent/30 border border-primary/20 mb-6">
                <p className="text-sm text-muted-foreground leading-relaxed">
                  Click the link in the email to verify your account. The link will expire in 1 hour.
                </p>
              </div>

              <div className="space-y-3">
                <Button
                  variant="outline"
                  className="w-full"
                  onClick={() => setEmailSent(false)}
                >
                  Resend email
                </Button>
                <button
                  onClick={() => onNavigate && onNavigate("signin")}
                  className="text-sm text-primary hover:underline w-full"
                >
                  Already have an account? Sign in
                </button>
              </div>
            </Card>
          </motion.div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center px-4 sm:px-6 lg:px-8 gradient-mesh">
      <div className="w-full max-w-md">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          {/* Logo */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center gap-2 mb-4">
              <div className="w-12 h-12 rounded-xl gradient-green flex items-center justify-center shadow-lg">
                <Leaf className="w-7 h-7 text-white" />
              </div>
            </div>
            <h1 className="text-3xl mb-2">
              {invitation ? 'Accept Team Invitation' : 'Create an account'}
            </h1>
            <p className="text-muted-foreground">
              {invitation 
                ? `Join ${invitation.clinic_name} on CannaBias`
                : 'Join CannaBias to review cannabis products'
              }
            </p>
          </div>

          {/* Invitation Banner */}
          {invitationLoading && (
            <Card className="p-4 mb-6 border-primary/20">
              <div className="flex items-center gap-3">
                <Loader2 className="h-5 w-5 animate-spin text-primary" />
                <p className="text-sm">Loading invitation details...</p>
              </div>
            </Card>
          )}

          {invitationError && (
            <Alert variant="destructive" className="mb-6">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{invitationError}</AlertDescription>
            </Alert>
          )}

          {invitation && !invitationError && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="mb-6"
            >
              <Card className="p-5 border-primary/30 bg-primary/5">
                <div className="flex items-start gap-3">
                  <div className="p-2 rounded-lg bg-primary/10">
                    <UserPlus className="h-5 w-5 text-primary" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <Building2 className="h-4 w-4 text-muted-foreground" />
                      <span className="font-medium">{invitation.clinic_name}</span>
                    </div>
                    <p className="text-sm text-muted-foreground mb-2">
                      {invitation.invited_by_name} invited you to join as{' '}
                      <Badge variant="secondary" className="ml-1">
                        {invitation.role}
                      </Badge>
                    </p>
                    <p className="text-xs text-muted-foreground">
                      Invitation expires{' '}
                      {new Date(invitation.expires_at).toLocaleDateString('en-GB', {
                        day: 'numeric',
                        month: 'short',
                        year: 'numeric',
                      })}
                    </p>
                  </div>
                </div>
              </Card>
            </motion.div>
          )}

          {/* Form */}
          <Card className="p-8 glass-card">
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <Label htmlFor="displayName" className="flex items-center gap-2">
                  <User className="w-4 h-4" />
                  Display Name
                </Label>
                <Input
                  id="displayName"
                  type="text"
                  placeholder="Enter your display name"
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                  className="mt-2"
                  required
                  disabled={loading}
                />
                <p className="text-xs text-muted-foreground mt-2">
                  This pseudonym protects your privacy
                </p>
              </div>

              <div>
                <Label htmlFor="email" className="flex items-center gap-2">
                  <Mail className="w-4 h-4" />
                  Email Address
                </Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="Enter your email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="mt-2"
                  required
                  disabled={loading || !!invitation}
                />
                <p className="text-xs text-muted-foreground mt-2">
                  {invitation 
                    ? 'Email is set by the invitation and cannot be changed'
                    : "We'll send you a magic link to sign in"
                  }
                </p>
              </div>

              <Button
                type="submit"
                className="w-full gradient-green hover:opacity-90 transition-opacity h-12"
                disabled={loading}
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Sending magic link...
                  </>
                ) : (
                  <>
                    <Mail className="w-4 h-4 mr-2" />
                    Send magic link
                  </>
                )}
              </Button>
            </form>

            <div className="mt-6 text-center">
              <p className="text-sm text-muted-foreground">
                Already have an account?{" "}
                <button
                  onClick={() => onNavigate && onNavigate("signin")}
                  className="text-primary hover:underline"
                  disabled={loading}
                >
                  Sign in
                </button>
              </p>
            </div>

            <div className="mt-6 p-4 rounded-xl bg-accent/30 border border-primary/20">
              <p className="text-xs text-muted-foreground leading-relaxed">
                By creating an account, you agree to our Terms of Service and Privacy Policy. 
                You must be over 18 years of age to access the service.
              </p>
            </div>

            {/* 🔧 DEV: Skip to GDPR screen */}
            <div className="mt-4">
              <Button
                variant="outline"
                size="sm"
                className="w-full"
                onClick={() => onNavigate && onNavigate("consent")}
                type="button"
              >
                <ArrowRight className="w-4 h-4 mr-2" />
                Skip to GDPR Screen (Dev)
              </Button>
            </div>
          </Card>
        </motion.div>
      </div>
    </div>
  );
}
